import React, { useEffect, useRef, useState } from 'react'
import { EditTaskModalProps } from '../types'
import { Button, Form, Modal, Toast } from '@seiue/ui'
import { TaskHeaderLeft } from '@/components/TaskHeaderLeft'
import styled from 'styled-components'
import { EntryComponentsWrapper } from 'packages/theme'
import { BaseSetting } from './BaseSetting'
import {
  QuizTypeEnum,
  TaskStatusEnum,
  TaskTypeEnum,
  activityApi,
  quizApi,
} from 'packages/sdks-next/growp'
import {
  CommonBasicInfo,
  CommonBasicInfoRef,
  SubmitValues,
} from '@/components/CommonBasicInfo'
import { pick } from '@seiue/util'
import moment from 'moment'
import { ModalLayout } from '@/components/ModalLayout'
import { getTheme } from '@/utils/theme'

/**
 * 新增|编辑-测验
 * @param param - 参数
 * @param param.taskId - 任务ID
 * @param param.activityId - 活动ID
 * @param param.onClose - 关闭弹窗方法
 * @param param.activityTaskOptions - 活动任务选项
 * @returns React.ReactNode
 */
export const EditQuizTaskModal: React.FC<EditTaskModalProps> = ({
  onClose,
  activityId,
  taskId,
  activityTaskOptions,
  domain,
}) => {
  const theme = getTheme()
  const isEdit = !!taskId
  const [form] = Form.useForm()
  const [paperId, setPaperId] = useState<number | undefined>(undefined)
  const basicInfoRef = useRef<CommonBasicInfoRef>(null)
  const [commonBasicInfo, setCommonBasicInfo] = React.useState<SubmitValues>({})
  const [isNormalMode, setIsNormalMode] = useState<boolean>(true)
  const [title, setTitle] = useState<string>('未命名测验')
  const { data: quizTask } = quizApi.useLoadQuizActivityTask(
    {
      id: taskId || 0,
    },
    {
      disable: !taskId,
    }
  )

  const userIsSubmit =
    !!quizTask?.userCompletedCount && quizTask?.userCompletedCount > 0

  const { api: createQuizActivityTask } = quizApi.useCreateQuizActivityTask({
    reload: [activityApi.queryTasks, activityApi.loadActivity],
  })

  const { api: useUpdateQuizActivityTask } = quizApi.useUpdateQuizActivityTask({
    reload: [activityApi.queryTasks, activityApi.loadActivity],
  })

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        type: QuizTypeEnum.Normal,
        showName: true,
        showAnswer: true,
        againstCheat: true,
      })
    }
  }, [])

  useEffect(() => {
    form.setFieldsValue({
      ...quizTask?.quiz,
      passScoreRate: quizTask?.quiz?.passScoreRate
        ? Number(quizTask?.quiz?.passScoreRate)
        : undefined,
      name: quizTask?.task?.name,
      description: quizTask?.task?.description,
    })
    setPaperId(quizTask?.quiz?.paperId)
    setIsNormalMode(
      quizTask?.quiz?.type === QuizTypeEnum.Normal || !quizTask?.quiz?.type
    )
    setTitle(quizTask?.task?.name || '未命名测验')
  }, [quizTask?.quiz, quizTask?.task])

  useEffect(() => {
    setCommonBasicInfo({
      ...pick({ ...quizTask?.task }, [
        'endAt',
        'adminIds',
        'timedStartAt',
        'prevTaskIds',
      ]),
      startAt: quizTask?.task
        ? quizTask?.task.startAt
        : moment().format('YYYY-MM-DD HH:mm:ss'),
      isShow:
        quizTask?.task?.isShow !== undefined ? quizTask?.task?.isShow : true,
    })
  }, [quizTask?.task])

  const onSubmit = () => {
    form.validateFields().then(async (values) => {
      if (!paperId) {
        Toast.warning('请先创建试卷')
        return
      }
      const basicInfo = basicInfoRef.current?.getBasicInfo()
      const task = {
        ...basicInfo,
        activityId,
        type: TaskTypeEnum.Quiz,
        status: TaskStatusEnum.Published,
        name: values.name,
        description: values.description,
      }
      const quiz = {
        ...values,
        paperId,
        passScoreRate: values.passScoreRate
          ? String(values.passScoreRate)
          : undefined,
        id: isEdit ? quizTask?.quiz?.id : undefined,
      }
      const payload = {
        task,
        quiz,
      }
      if (isEdit) {
        const { data } = await quizApi.checkQuizActivityTaskCompletedInfo(
          taskId
        )
        if (data && data > 0) {
          Modal.confirm({
            title: '确定保存修改吗?',
            content: `已有 ${data} 位成员完成了此测验；保存修改并发布活动后，成员需重新完成此任务，确认要继续吗？`,
            onOk: async () => {
              await useUpdateQuizActivityTask({ id: taskId, payload })
              Toast.success('保存成功')
              onClose()
            },
          })
        } else {
          await useUpdateQuizActivityTask({ id: taskId, payload })
          Toast.success('保存成功')
          onClose()
        }
      } else {
        await createQuizActivityTask(payload)
        Toast.success('保存成功')
        onClose()
      }
    })
  }
  return (
    <ModalLayout
      title={
        <TaskHeaderLeft
          title={title || '未命名测验'}
          onClose={() => {
            Modal.confirm({
              title: '确定关闭此页面吗？',
              content: '退出后后当前修改不会被保存，是否确定退出？',
              onOk: () => {
                onClose()
              },
              okButtonProps: {
                style: {
                  backgroundColor: theme.brand._1,
                  border: 'none',
                },
              },
              cancelButtonProps: {
                style: {
                  color: theme.text._2,
                },
              },
            })
          }}
        />
      }
      headerRight={
        <>
          <Button
            type="primary"
            onClick={onSubmit}
          >
            {isEdit ? '更新' : '确认'}
          </Button>
        </>
      }
      contentStyle={{
        padding: '0 0 20px 0',
      }}
      content={
        <EntryComponentsWrapper>
          <Form
            form={form}
            labelAlign="right"
            colon={false}
          >
            <ContentCard>
              <BaseSetting
                form={form}
                isEdit={isEdit}
                setTitle={(v) => setTitle(v)}
                isNormalMode={isNormalMode}
                setIsNormalMode={(v) => setIsNormalMode(v)}
                paperId={paperId}
                setPaperId={(v) => setPaperId(v)}
                userIsSubmit={userIsSubmit}
              />
            </ContentCard>
            <ContentCard>
              <CommonBasicInfo
                form={form}
                ref={basicInfoRef}
                value={commonBasicInfo}
                activityTaskOptions={activityTaskOptions || []}
                openTypeDisabled={userIsSubmit}
              />
            </ContentCard>
          </Form>
        </EntryComponentsWrapper>
      }
    />
  )
}

const ContentCard = styled.div`
  width: 960px;
  min-height: 218px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
  padding: 24px 32px;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0px 0px 1px 0px rgba(21, 29, 41, 0.2),
    0px 3px 8px -1px rgba(21, 29, 41, 0.04);
  &&& {
    .ant-form-item {
      margin-bottom: 0;
    }
    .ant-form-item-label {
      width: 88px;
      margin-right: 0;
      padding-right: 20px;
    }
    .ant-form-item-label > label.ant-form-item-no-colon::after {
      margin-right: 0;
    }
  }
`
